import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Phone, Calendar, Check, X as XIcon, Clock } from 'lucide-react';

interface Slot {
    id: number;
    fecha: string; // 'YYYY-MM-DD'
    hora_inicio: string;
    hora_fin: string;
    estado: 'disponible' | 'reservado' | 'bloqueado';
    nota: string;
    cliente_nombre: string;
}

interface CalendarioDisponibilidadProps {
    slots: Slot[];
    readOnly?: boolean;
    onSlotClick?: (date: string, currentSlot?: Slot) => void;
    onSlotDelete?: (slotId: number) => void;
    /** Called when a day cell is clicked (admin mode) — receives ALL slots for that day */
    onDayClick?: (date: string, daySlots: Slot[]) => void;
    /** IDs of slots the user has selected (booking mode) */
    selectedSlotIds?: (string | number)[];
}

const DAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const CalendarioDisponibilidad: React.FC<CalendarioDisponibilidadProps> = ({
    slots,
    readOnly = true,
    onSlotClick,
    onSlotDelete,
    onDayClick,
    selectedSlotIds = []
}) => {
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    /** expanded day for public view (shows time slots) */
    const [expandedDay, setExpandedDay] = useState<string | null>(null);

    const goToPrev = () => {
        if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
        else { setCurrentMonth(m => m - 1); }
    };
    const goToNext = () => {
        if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
        else { setCurrentMonth(m => m + 1); }
    };

    // Build calendar grid
    const calendarDays = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const daysInMonth = lastDay.getDate();
        let startDayOfWeek = firstDay.getDay() - 1;
        if (startDayOfWeek < 0) startDayOfWeek = 6;
        const days: (number | null)[] = [];
        for (let i = 0; i < startDayOfWeek; i++) days.push(null);
        for (let d = 1; d <= daysInMonth; d++) days.push(d);
        return days;
    }, [currentMonth, currentYear]);

    // Sort helper: treat times before 06:00 as after-midnight (sort after 22:00)
    const sortKey = (t: string) => {
        const h = parseInt(t || '99', 10);
        return h < 6 ? String(h + 24).padStart(2, '0') + t.slice(2) : t;
    };

    // Index slots by date — returns ARRAY of slots per day, sorted smartly
    const slotsByDate = useMemo(() => {
        const map: Record<string, Slot[]> = {};

        // Populate actual DB slots
        for (const s of slots) {
            if (!map[s.fecha]) map[s.fecha] = [];
            map[s.fecha].push(s);
        }

        // Generate virtual "Reservado" slots for days that have nothing to make it look busy.
        // We'll generate slots from 18:00 to 02:00 for the current month view.
        const addVirtualSlots = (dateStr: string) => {
            if (!map[dateStr]) map[dateStr] = [];
            const defaultHours = [
                { i: '18:00', f: '20:00' },
                { i: '20:00', f: '22:00' },
                { i: '22:00', f: '00:00' },
                { i: '00:00', f: '02:00' }
            ];

            for (const h of defaultHours) {
                // Check if there is already a slot (real or virtual) that overlaps this time
                const exists = map[dateStr].some(s => s.hora_inicio.startsWith(h.i.split(':')[0]));
                if (!exists) {
                    map[dateStr].push({
                        id: -Math.floor(Math.random() * 1000000), // temp negative ID for virtual slots
                        fecha: dateStr,
                        hora_inicio: h.i,
                        hora_fin: h.f,
                        estado: 'reservado',
                        nota: 'Reservado (Virtual)',
                        cliente_nombre: ''
                    });
                }
            }
        };

        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);

        // Only add virtual slots for days from today onwards to avoid cluttering past days unnecessarily
        const todayStr = new Date().toISOString().split('T')[0];

        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            if (dateStr >= todayStr) {
                addVirtualSlots(dateStr);
            }
        }

        // Sort all days
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) => sortKey(a.hora_inicio || '').localeCompare(sortKey(b.hora_inicio || '')));
        }
        return map;
    }, [slots, currentMonth, currentYear]);

    const formatDate = (day: number): string => {
        const m = String(currentMonth + 1).padStart(2, '0');
        const d = String(day).padStart(2, '0');
        return `${currentYear}-${m}-${d}`;
    };

    const isToday = (day: number): boolean =>
        day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();

    const isPast = (day: number): boolean => {
        const date = new Date(currentYear, currentMonth, day);
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return date < todayStart;
    };

    const getDaySummary = (daySlots: Slot[]) => {
        const available = daySlots.filter(s => s.estado === 'disponible').length;
        const reserved = daySlots.filter(s => s.estado === 'reservado').length;
        const total = daySlots.length;
        return { available, reserved, total };
    };

    const getDayStyle = (daySlots: Slot[], day: number) => {
        if (isPast(day)) return 'bg-gray-100 text-gray-300 cursor-not-allowed';
        if (!daySlots || daySlots.length === 0) {
            if (readOnly) return 'bg-white text-gray-400 hover:bg-gray-50';
            return 'bg-white text-gray-400 hover:bg-blue-50 cursor-pointer border-dashed';
        }
        const { available, reserved } = getDaySummary(daySlots);
        if (available > 0 && reserved === 0) return 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 cursor-pointer ring-1 ring-emerald-300';
        if (reserved > 0 && available === 0) return 'bg-red-50 text-red-700 border-red-200 cursor-pointer';
        if (available > 0 && reserved > 0) return 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 cursor-pointer ring-1 ring-amber-300';
        return 'bg-gray-200 text-gray-500 border-gray-300';
    };

    const handleCellClick = (day: number) => {
        if (isPast(day)) return;
        const dateStr = formatDate(day);
        const daySlots = slotsByDate[dateStr] || [];

        if (readOnly) {
            // Public mode: toggle expanded view for this day
            if (daySlots.some(s => s.estado === 'disponible')) {
                setExpandedDay(expandedDay === dateStr ? null : dateStr);
            }
            return;
        }

        // Admin mode: pass all day slots to the onDayClick handler
        if (onDayClick) {
            onDayClick(dateStr, daySlots);
        } else if (onSlotClick) {
            // Legacy fallback — click first slot or create new
            onSlotClick(dateStr, daySlots[0]);
        }
    };

    return (
        <div className="w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <button onClick={goToPrev} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
                    <ChevronLeft size={24} className="text-gray-600" />
                </button>
                <div className="text-center">
                    <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-900">
                        {MONTHS_ES[currentMonth]} {currentYear}
                    </h3>
                    {readOnly && (
                        <p className="text-sm text-gray-500 mt-1">
                            <Calendar size={14} className="inline mr-1" />
                            Pulsa en un día disponible para ver las horas
                        </p>
                    )}
                </div>
                <button onClick={goToNext} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
                    <ChevronRight size={24} className="text-gray-600" />
                </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_ES.map(day => (
                    <div key={day} className="text-center text-xs font-bold uppercase tracking-wider text-gray-400 py-2">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, idx) => {
                    if (day === null) return <div key={`empty-${idx}`} className="aspect-square" />;

                    const dateStr = formatDate(day);
                    const daySlots = slotsByDate[dateStr] || [];
                    const past = isPast(day);
                    const { available, reserved, total } = getDaySummary(daySlots);

                    return (
                        <button
                            key={dateStr}
                            onClick={() => handleCellClick(day)}
                            disabled={past || (readOnly && available === 0)}
                            className={`
                                aspect-square rounded-xl border transition-all duration-200 flex flex-col items-center justify-center gap-0 relative overflow-hidden
                                ${getDayStyle(daySlots, day)}
                                ${isToday(day) ? 'ring-2 ring-blue-400 ring-offset-1' : ''}
                                ${!past && !readOnly ? 'hover:scale-105 active:scale-95' : ''}
                            `}
                            title={
                                total > 0
                                    ? `${available} disponible(s), ${reserved} reservado(s)`
                                    : readOnly ? 'Sin programar' : 'Click para gestionar horas'
                            }
                        >
                            <span className={`text-sm md:text-base font-bold ${isToday(day) ? 'text-blue-600' : ''}`}>
                                {day}
                            </span>
                            {/* Show slot count badges */}
                            {total > 0 && (
                                <div className="flex items-center gap-0.5 mt-0.5">
                                    {available > 0 && (
                                        <span className="text-[8px] font-black bg-emerald-500 text-white rounded px-1 leading-tight">
                                            {available}
                                        </span>
                                    )}
                                    {reserved > 0 && (
                                        <span className="text-[8px] font-black bg-red-500 text-white rounded px-1 leading-tight">
                                            {reserved}
                                        </span>
                                    )}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Expanded day — public view: shows time slots for selected day */}
            {readOnly && expandedDay && slotsByDate[expandedDay] && (
                <div className="mt-4 bg-white rounded-2xl border border-gray-200 shadow-lg p-5 animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <Clock size={16} className="text-indigo-500" />
                            Horarios — {new Date(expandedDay + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </h4>
                        <button onClick={() => setExpandedDay(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                            <XIcon size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {slotsByDate[expandedDay].map(slot => {
                            const isAvailable = slot.estado === 'disponible';
                            const isReserved = slot.estado === 'reservado';
                            return (
                                <button
                                    key={slot.id}
                                    onClick={() => { if (isAvailable && onSlotClick) onSlotClick(expandedDay, slot); }}
                                    disabled={!isAvailable}
                                    className={`
                                        px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5
                                        ${selectedSlotIds.includes(slot.id)
                                            ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-lg ring-2 ring-indigo-300'
                                            : isAvailable
                                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 hover:shadow-md cursor-pointer'
                                                : isReserved
                                                    ? 'bg-red-50 text-red-400 border border-red-200 cursor-not-allowed line-through'
                                                    : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                        }
                                    `}
                                >
                                    {selectedSlotIds.includes(slot.id) ? <Check size={13} /> : <Clock size={13} />}
                                    {slot.hora_inicio?.slice(0, 5)} – {slot.hora_fin?.slice(0, 5)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-6 justify-center text-xs font-medium">
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-emerald-100 border border-emerald-300"></div>
                    <span className="text-gray-600">Disponible</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
                    <span className="text-gray-600">Reservado</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                    <span className="text-gray-600">No disponible</span>
                </div>
                {readOnly && (
                    <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 rounded bg-white border border-gray-200"></div>
                        <span className="text-gray-600">Sin programar</span>
                    </div>
                )}
            </div>


        </div>
    );
};
